import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, AlertCircle, Clock, X } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import FAQSection from '../../components/FAQSection';
import { Turnstile } from '@marsidev/react-turnstile'; // [TURNSTILE] Import thư viện

interface ContactProps {
  language: string;
  isModal?: boolean;
  onClose?: () => void;
  onOpenTerms?: () => void;   // Thêm hàm mở Terms
  onOpenPrivacy?: () => void; // Thêm hàm mở Privacy
}

const Contact: React.FC<ContactProps> = ({ language, isModal = false, onClose, onOpenTerms, onOpenPrivacy }) => {
  // Form States
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [agreed, setAgreed] = useState(false);
  const [showAgreementError, setShowAgreementError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  // [TURNSTILE] State lưu token xác thực
  const [turnstileToken, setTurnstileToken] = useState('');

  // Khóa cuộn trang khi mở Modal
  useEffect(() => {
    if (isModal) {
      const originalBodyStyle = document.body.style.overflow;
      const originalHtmlStyle = document.documentElement.style.overflow;
      
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalBodyStyle;
        document.documentElement.style.overflow = originalHtmlStyle;
      };
    }
  }, [isModal]);

  const translations = {
    en: {
      title: 'Contact Information',
      formTitle: 'Wishing you a great day! I am here to listen to you.',
      namePlaceholder: 'Your Name (Optional)',
      emailPlaceholder: 'Your Email Address',
      messagePlaceholder: 'Message...',
      sendButton: 'Send Message',
      sending: 'Sending...',
      successMessage: 'Thank you. Message sent successfully!',
      errorMessage: 'Failed to send message.',
      email: 'Gmail',
      phone: 'Phone',
      location: 'Location',
      feedbackQuestion: 'Is this information helpful?',
      thankYou: 'Thank you!',
      responseNote: 'Response within 24h',
      agreeTerms: (
        <span>
          I agree with the <span onClick={(e) => { e.stopPropagation(); onClose?.(); onOpenTerms?.(); }} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold cursor-pointer">Term of Use</span> and <span onClick={(e) => { e.stopPropagation(); onClose?.(); onOpenPrivacy?.(); }} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold cursor-pointer">Privacy Policy</span>
        </span>
      ),
      agreementRequired: "Please agree to the terms.",
      captchaRequired: "Please verify that you are human.",
    },
    vi: {
      title: 'THÔNG TIN LIÊN HỆ',
      formTitle: 'Chúc bạn ngày mới tốt lành! Mình ở đây lắng nghe bạn.',
      namePlaceholder: 'Tên của bạn (Tùy chọn)',
      emailPlaceholder: 'Email của bạn',
      messagePlaceholder: 'Nội dung...',
      sendButton: 'Gửi Tin Nhắn',
      sending: 'Đang gửi...',
      successMessage: 'Cảm ơn bạn. Đã gửi thành công!',
      errorMessage: 'Gửi thất bại.',
      email: 'Gmail',
      phone: 'Điện Thoại',
      location: 'Địa Chỉ',
      feedbackQuestion: 'Thông tin này có hữu ích không?',
      thankYou: 'Cảm ơn bạn!',
      responseNote: 'Phản hồi trong vòng 24h',
      agreeTerms: (
        <span>
          Tôi đồng ý với <span onClick={(e) => { e.stopPropagation(); onClose?.(); onOpenTerms?.(); }} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold cursor-pointer">Điều khoản</span> và <span onClick={(e) => { e.stopPropagation(); onClose?.(); onOpenPrivacy?.(); }} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold cursor-pointer">Chính sách bảo mật</span>
        </span>
      ),
      agreementRequired: "Vui lòng đồng ý điều khoản trước khi gửi.",
      captchaRequired: "Vui lòng xác minh bạn không phải là robot.",
    }
  };

  const t = translations[language as keyof typeof translations];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreed(e.target.checked);
    if (e.target.checked) setShowAgreementError(false);
  };

  const sanitizeInput = (str: string) => {
    if (!str) return '';
    return str.replace(/<[^>]*>?/gm, ''); 
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) { setShowAgreementError(true); return; }
    
    if (!turnstileToken) {
      alert(t.captchaRequired);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const cleanData = {
      name: sanitizeInput(formData.name).substring(0, 200),
      email: sanitizeInput(formData.email).substring(0, 200),
      message: sanitizeInput(formData.message).substring(0, 1000),
      turnstileToken: turnstileToken
    };

    try {
      const response = await fetch('/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify(cleanData), 
      });
      if (!response.ok) throw new Error('Failed to send');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setAgreed(false);
      trackEvent('contact_form', 'submit', 'success');
    } catch (error) {
      console.error(error);
      setSubmitStatus('error');
      trackEvent('contact_form', 'submit', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <Mail size={24} className="text-blue-600 dark:text-blue-400" />, title: t.email, value: 'ntainguyen.it@gmail.com', link: 'mailto:ntainguyen.it@gmail.com' },
    { icon: <Phone size={24} className="text-blue-600 dark:text-blue-400" />, title: t.phone, value: '(+84) 366 063 944', link: 'tel:+84366063944' },
    { icon: <MapPin size={24} className="text-blue-600 dark:text-blue-400" />, title: t.location, value: 'Hue City, Vietnam', link: 'https://maps.google.com/?q=Hue,Vietnam' }
  ];

  const handleContactClick = (method: string) => { trackEvent('contact_click', 'Contact', method); };

  const renderFormContent = () => (
    <div className={`w-full max-w-4xl mx-auto ${isModal ? '' : 'mb-16'}`} data-aos={!isModal ? "fade-up" : ""}>
        <div className={`
            bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md rounded-2xl 
            ${isModal ? 'shadow-none border-0 bg-transparent p-0' : 'shadow-sm p-8 border border-slate-200/60 dark:border-zinc-800'}
        `}>
            <h3 className={`font-bold text-gray-800 dark:text-white text-center 
                ${isModal ? 'text-lg md:text-xl mb-4 text-gray-600 dark:text-gray-300 font-medium' : 'text-2xl mb-6'}
            `}>
                {t.formTitle}
            </h3>
            
            <form onSubmit={handleSubmit} className={isModal ? "space-y-3" : "space-y-4"}>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={t.namePlaceholder}
                    maxLength={200}
                    className={`w-full rounded-lg bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-zinc-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all
                    ${isModal ? 'px-4 py-3 text-sm' : 'px-4 py-3 text-base'}`} />
                
                <div className="relative">
                    <input type="email" name="email" required value={formData.email} onChange={handleInputChange} placeholder={t.emailPlaceholder}
                        maxLength={200}
                        className={`w-full rounded-lg bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-zinc-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all pl-4
                        ${isModal ? 'px-4 py-3 text-sm' : 'px-4 py-3 text-base'}`} />
                    <span className="absolute top-1/2 -translate-y-1/2 right-4 text-red-500 text-xl leading-none" title="Required">*</span>
                </div>

                <textarea name="message" required rows={isModal ? 4 : 4} value={formData.message} onChange={handleInputChange} placeholder={t.messagePlaceholder}
                    maxLength={1000}
                    className={`w-full rounded-lg bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-zinc-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none
                    ${isModal ? 'px-4 py-3 text-sm' : 'px-4 py-3 text-base'}`}></textarea>

                <div className="flex items-start gap-3 px-1">
                    <div className="flex items-center h-5">
                        <input id={`terms-${isModal ? 'modal' : 'section'}`} type="checkbox" checked={agreed} onChange={handleCheckboxChange}
                            className="w-4 h-4 text-blue-600 cursor-pointer" />
                    </div>
                    <label htmlFor={`terms-${isModal ? 'modal' : 'section'}`} className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                        {t.agreeTerms}
                    </label>
                </div>

                {showAgreementError && (
                    <div className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/10 p-2 rounded-md text-xs animate-pulse">
                        <AlertCircle size={14} /> <span>{t.agreementRequired}</span>
                    </div>
                )}

                <div className="flex justify-center md:justify-start my-4 md:px-1">
                  <Turnstile 
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY} 
                    onSuccess={(token) => setTurnstileToken(token)}
                  />
                </div>

                <button type="submit" disabled={isSubmitting}
                    className={`w-full rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold shadow-lg shadow-blue-500/30 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed
                    ${isModal ? 'py-3 text-sm uppercase tracking-wide' : 'py-3 text-base'}`}>
                    {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> {t.sending}</> : <><Send size={18} /> {t.sendButton}</>}
                </button>
                
                <div className="flex items-center justify-center gap-2 mt-3 text-xs md:text-sm text-gray-500 dark:text-gray-400 italic">
                    <Clock size={16} /> <span>{t.responseNote}</span>
                </div>
            </form>

            {submitStatus === 'success' && (
                <div className="mt-1 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-3 text-green-600 dark:text-green-400 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="bg-green-500 rounded-full p-1 text-white shadow-lg shadow-green-500/30">
                        <CheckCircle size={16} />
                    </div>
                    <p className="text-sm font-black italic tracking-tight">{t.successMessage}</p>
                </div>
            )}
            {submitStatus === 'error' && (
                <div className="mt-3 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm animate-in fade-in slide-in-from-top-2">
                    <AlertCircle size={16} /> <span>{t.errorMessage}</span>
                </div>
            )}
        </div>
    </div>
  );

  const renderInfoCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl mx-auto">
      {contactInfo.map((info, index) => (
        <a key={index} href={info.link} target="_blank" rel="noopener noreferrer"
           className="bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-2xl border border-slate-200/60 dark:border-zinc-800 shadow-sm p-6 flex flex-col items-center text-center transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:hover:shadow-[0_0_30px_rgba(96,165,250,0.5)] active:-translate-y-1 active:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:active:shadow-[0_0_30px_rgba(96,165,250,0.5)]"
           data-aos="fade-up" data-aos-delay={index * 100} onClick={() => handleContactClick(info.title)}>
          <div className="p-4 bg-blue-100 dark:bg-blue-900 rounded-full mb-4 shadow-md">{info.icon}</div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">{info.title}</h3>
          <p className="text-gray-600 dark:text-gray-300">{info.value}</p>
        </a>
      ))}
    </div>
  );

  if (isModal) {
    return createPortal(
      <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
        <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in" 
            onClick={onClose}
        ></div>
        
        <div className="relative w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 ease-out border border-slate-200/60 dark:border-zinc-800">


           <button 
                onClick={onClose} 
                className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-red-500 text-gray-500 hover:text-white rounded-full transition-all duration-200 z-10 shadow-sm group"
                title="Close"
            >
             <X size={20} className="group-hover:rotate-90 transition-transform duration-200" />
           </button>

           <div className="p-6 md:p-8">
               <h2 className={`text-3xl text-center mb-6 uppercase text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 py-4 font-black tracking-tighter leading-normal`}>
                  {t.title}
               </h2>
               {renderFormContent()}
           </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <section id="contact" className="py-20 px-6 rounded">
      <div className="max-w-5xl mx-auto flex flex-col items-center">
        <div className="text-center mb-12 w-full" data-aos="fade-up">
          <h2 className={`text-5xl md:text-6xl text-center mb-4 uppercase text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-200 dark:to-gray-400 py-4 font-black tracking-tighter leading-normal`}>
            {t.title}
          </h2>
          <div className="w-32 h-0.5 mx-auto mt-4" style={{ background: '#00ccff', boxShadow: '0 0 10px #00ccff, 0 0 20px #00ccff, 0 0 30px #00ccff' }}></div>
        </div>
        
        {renderInfoCards()}
        
        <FAQSection language={language} />

      </div>
    </section>
  );
};

export default React.memo(Contact);